import { useEffect, useState } from 'react';
import { Book } from '../types/book.types';
import { searchBooks } from '../services/BooksService';
import { listLowStockBooks, listActiveSuppliers, LowStockBook, Supplier } from '../services/PurchasingService';

/** SearchResults solo pinta resultados a partir de 2 caracteres. */
const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

/**
 * `enabled` a false apaga la busqueda sin desmontar el hook: en devoluciones el
 * mismo input busca facturas por id (useInvoiceSearch) y no hay que llamar a
 * books_list.
 */
/**
 * books_list con almacen solo devuelve libros que ya tuvieron movimientos en
 * el, asi que un libro a 0 que nunca entro ahi no sale. Se completan los
 * resultados con stock_levels_list (que si incluye los que nunca se movieron) y
 * a los que no tienen stock se les cuelgan los proveedores como "fuentes de
 * suministro": de ahi se les puede pedir el libro.
 *
 * Cualquier fallo de la parte de pedidos se ignora: la busqueda de venta nunca
 * debe romperse por ella.
 */
const searchWithOutOfStock = async (term: string): Promise<Book[]> => {
    const books = await searchBooks(term);

    const [lowStock, suppliers] = await Promise.all([
        listLowStockBooks({ search: term, maxStock: 0, pageSize: 10 })
            .then((r): LowStockBook[] => r.books)
            .catch((): LowStockBook[] => []),
        listActiveSuppliers().catch((): Supplier[] => [])
    ]);

    const known = new Set(books.map(b => b.id));
    const extra: Book[] = lowStock
        .filter(b => !known.has(b.id))
        .map(b => ({
            id: b.id,
            title: b.title,
            author: b.author,
            price: b.price,
            weight: 0,
            isbn: b.isbn,
            category: b.category,
            stock: Math.min(b.stock, 0),
            publisher: ''
        }));

    return books.concat(extra).map(book =>
        book.stock > 0
            ? book
            : { ...book, sources: suppliers.map(s => ({ id: s.id, name: s.name })) }
    );
};

export const useBookSearch = (query: string, enabled = true) => {
    const [books, setBooks] = useState<Book[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const term = query.trim();

        if (!enabled || term.length < MIN_QUERY_LENGTH) {
            setBooks([]);
            setError(null);
            setIsLoading(false);
            return;
        }

        // El input dispara setSearchQuery en cada tecla: sin debounce serian
        // ~15 peticiones por titulo. `cancelled` evita ademas que una respuesta
        // lenta pise a la de una query posterior.
        let cancelled = false;
        setIsLoading(true);
        setError(null);

        const timer = setTimeout(() => {
            searchWithOutOfStock(term)
                .then((list) => {
                    if (cancelled) return;
                    setBooks(list);
                    setIsLoading(false);
                })
                .catch((err) => {
                    if (cancelled) return;
                    setBooks([]);
                    setError(err?.message || 'No se pudo buscar en el catalogo.');
                    setIsLoading(false);
                });
        }, DEBOUNCE_MS);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [query, enabled]);

    return { books, isLoading, error };
};
